import { useEffect } from 'react';
import { TextField, useFormContext } from '@/components/form';
import { positiveIntegerFieldProps } from '@/components/form/numberInputProps';
import { useDebouncedValue } from '@/lib/useDebouncedValue';

/**
 * Box-height input for drawer forms, in millimetres.
 *
 * Only rendered for systems whose catalog row carries no H-box of its own —
 * Glisare runners, where the box is built rather than bought and the height is
 * a free dimension. Box systems take their H-box (N/M/K/C) from the selected
 * variant instead, and show it read-only.
 *
 * Clamps to the drawer's height when that shrinks below the current value.
 */
export function DrawerHboxField({ heightMm, disabled }: { heightMm: string; disabled?: boolean }) {
  const form = useFormContext();
  const debouncedHeight = useDebouncedValue(heightMm, 300);

  useEffect(() => {
    const height = parseInt(debouncedHeight, 10);
    const hBox = parseInt(form.getValues('hBox'), 10);
    if (!height || !hBox) return;
    if (hBox > height) {
      form.setValue('hBox', String(height), { shouldDirty: true, shouldValidate: true });
    }
  }, [form, debouncedHeight]);

  const maxH = parseInt(heightMm, 10);

  return (
    <TextField
      name="hBox"
      label="H-box (mm) *"
      {...positiveIntegerFieldProps}
      max={maxH > 0 ? maxH : undefined}
      placeholder={heightMm ? `max ${heightMm}` : ''}
      required
      disabled={disabled}
      onChange={() => form.trigger('hBox')}
    />
  );
}
